import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import { Loader2, CheckCircle2 } from "lucide-react";
import { adminAnalyticsService, AdminSnapshot, ListSnapshotsResponse } from "@/services/admin/admin-analytics.admin.service";
import { DataTable, DataTablePagination } from "@/components/ui/data-table";

const columns: ColumnDef<AdminSnapshot>[] = [
  {
    id: "createdAt",
    header: "Created",
    cell: ({ row }) => <span>{new Date(row.original.createdAt).toLocaleString()}</span>,
    meta: { width: "w-44" },
  },
  {
    id: "status",
    header: "Status",
    cell: ({ row }) =>
      row.original.status === "IN_PROGRESS" ? (
        <span className="inline-flex items-center gap-2 text-amber-600 dark:text-amber-400">
          <Loader2 className="animate-spin" size={16} />
          <span>Calculating...</span>
        </span>
      ) : (
        <span className="inline-flex items-center gap-2 text-green-600 dark:text-green-400">
          <CheckCircle2 size={16} />
          <span>COMPLETED</span>
        </span>
      ),
    meta: { width: "w-40" },
  },
  {
    id: "timeframe",
    header: "Timeframe",
    cell: ({ row }) => (
      <span>
        {new Date(row.original.timeframeStart).toLocaleDateString()} —{" "}
        {new Date(row.original.timeframeEnd).toLocaleDateString()}
      </span>
    ),
    meta: { width: "w-48" },
  },
  {
    id: "brands",
    header: "Brands (active/total)",
    cell: ({ row }) => (
      <span>
        {row.original.activeBrands} / {row.original.totalBrands}
      </span>
    ),
  },
  {
    id: "affiliates",
    header: "Affiliates",
    cell: ({ row }) => <span>{row.original.totalAffiliates}</span>,
    meta: { width: "w-24" },
  },
  {
    id: "affiliateSales",
    header: "Affiliate Sales",
    cell: ({ row }) => (
      <span>
        {row.original.totalAffiliateSalesCount} /{" "}
        <span>
          $
          {row.original.totalAffiliateSalesAmount.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </span>
      </span>
    ),
  },
  {
    id: "campaignSales",
    header: "Campaign Sales",
    cell: ({ row }) => (
      <span>
        {row.original.totalCampaignSalesCount} /{" "}
        <span>
          $
          {row.original.totalCampaignSalesAmount.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </span>
      </span>
    ),
  },
  {
    id: "notes",
    header: "Notes",
    cell: ({ row }) => <span>{row.original.notes || "-"}</span>,
  },
];

export default function AdminSnapshots() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snapshots, setSnapshots] = useState<AdminSnapshot[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState<ListSnapshotsResponse["pagination"] | null>(null);
  const pollTimer = useRef<number | null>(null);

  const hasInProgress = useMemo(
    () => snapshots.some((s) => s.status === "IN_PROGRESS"),
    [snapshots]
  );

  const fetchSnapshots = useCallback(async () => {
    try {
      setError(null);
      const res = await adminAnalyticsService.listSnapshots({ page, limit: pageSize });
      setSnapshots(res.data);
      setPagination(res.pagination);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [page, pageSize]);

  useEffect(() => {
    fetchSnapshots();
  }, [fetchSnapshots]);

  // Poll every 45s if any snapshot is IN_PROGRESS
  useEffect(() => {
    if (hasInProgress) {
      pollTimer.current = window.setInterval(() => {
        fetchSnapshots();
      }, 45000);
    } else if (pollTimer.current) {
      window.clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
    return () => {
      if (pollTimer.current) {
        window.clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
    };
  }, [hasInProgress, fetchSnapshots]);

  const onGenerate = async () => {
    try {
      setLoading(true);
      setError(null);
      await adminAnalyticsService.generateSnapshot();
      await fetchSnapshots();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between bg-card border border-border rounded-xl p-4 lg:p-6 shadow-sm">
        <div>
          <h1 className="text-xl lg:text-2xl font-semibold text-foreground">Admin Analytics Snapshots</h1>
          <p className="text-sm text-muted-foreground mt-1">Historical snapshots to track platform growth over time.</p>
        </div>
        <button
          onClick={onGenerate}
          disabled={loading}
          className="px-4 py-2 rounded-lg bg-[#d7785b] text-white hover:bg-[#c26a4f] disabled:opacity-60 transition-colors"
        >
          {loading ? "Starting..." : "Generate Snapshot"}
        </button>
      </div>

      {error && (
        <div className="text-red-600 dark:text-red-400 text-sm">{error}</div>
      )}

      <div className="bg-card border border-border rounded-xl overflow-hidden shadow-sm">
        <DataTable
          columns={columns}
          data={snapshots}
          getRowId={(row) => row.id}
          emptyText='No snapshots yet. Click "Generate Snapshot" to create one.'
        />
        {/* Pagination */}
        {pagination && (
          <DataTablePagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            total={pagination.total}
            pageSize={pagination.limit || pageSize}
            onPageSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            pageSizeOptions={[10, 20, 50, 100]}
            onPageChange={setPage}
            noun="snapshots"
          />
        )}
      </div>
    </div>
  );
}


